import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    isPrivateApplicationPath,
    useCookiePreferences,
} from '../privacy/cookieConsent';

export const GOOGLE_ANALYTICS_MEASUREMENT_ID = 'G-766C9LEL8K';
export const GOOGLE_ANALYTICS_SCRIPT_ID = 'ekc-google-analytics';

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: any[]) => void;
        [key: `ga-disable-${string}`]: boolean | undefined;
    }
}

const expireCookie = (name: string, domain?: string) => {
    const domainAttribute = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; Max-Age=0; path=/${domainAttribute}; SameSite=Lax`;
};

const clearGoogleAnalyticsCookies = () => {
    const cookieNames = document.cookie
        .split(';')
        .map((cookie) => cookie.trim().split('=')[0])
        .filter((name) => /^(_ga|_gid|_gat)/.test(name));
    const hostnameParts = window.location.hostname.split('.');
    const parentDomain = hostnameParts.length > 1
        ? `.${hostnameParts.slice(-2).join('.')}`
        : undefined;

    cookieNames.forEach((name) => {
        expireCookie(name);
        if (parentDomain) expireCookie(name, parentDomain);
    });
};

export const disableGoogleAnalytics = () => {
    window[`ga-disable-${GOOGLE_ANALYTICS_MEASUREMENT_ID}`] = true;
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
    document.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID)?.remove();
    clearGoogleAnalyticsCookies();
};

const ensureGoogleAnalytics = () => {
    window[`ga-disable-${GOOGLE_ANALYTICS_MEASUREMENT_ID}`] = false;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag(...args: any[]) {
        window.dataLayer?.push(args);
    };

    window.gtag('consent', 'update', { analytics_storage: 'granted' });

    if (!document.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = GOOGLE_ANALYTICS_SCRIPT_ID;
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_MEASUREMENT_ID}`;
        document.head.appendChild(script);
    }

    const script = document.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID) as HTMLScriptElement;
    if (script.dataset.configured !== 'true') {
        window.gtag('js', new Date());
        window.gtag('config', GOOGLE_ANALYTICS_MEASUREMENT_ID, {
            anonymize_ip: true,
            send_page_view: false,
        });
        script.dataset.configured = 'true';
    }
};

const GoogleAnalyticsTracking = () => {
    const location = useLocation();
    const preferences = useCookiePreferences();
    const canTrack = preferences?.analytics === true && !isPrivateApplicationPath(location.pathname);

    useEffect(() => {
        if (!canTrack) {
            disableGoogleAnalytics();
            return;
        }

        ensureGoogleAnalytics();
        window.gtag?.('event', 'page_view', {
            page_location: window.location.href,
            page_path: location.pathname + location.search,
            page_title: document.title,
        });
    }, [canTrack, location.pathname, location.search]);

    return null;
};

export default GoogleAnalyticsTracking;
